import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { Toaster } from "@/components/ui/sonner";
import NotificationToast from "@/components/notifications/NotificationToast";
import { GoogleAnalytics, trackPageView } from "@/components/seo";
import { useEffect } from "react";

// Layouts
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import DashboardLayout from "@/components/layout/DashboardLayout";

// Public pages
import Landing from "@/pages/public/Landing";
import Features from "@/pages/public/Features";
import Pricing from "@/pages/public/Pricing";

// Auth pages
import SignUp from "@/pages/auth/SignUp";
import SignIn from "@/pages/auth/SignIn";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthCallback from "@/pages/auth/AuthCallback";

// Onboarding
import Step1 from "@/pages/onboarding/Step1";
import Step2 from "@/pages/onboarding/Step2";
import Step3 from "@/pages/onboarding/Step3";
import Step4 from "@/pages/onboarding/Step4";

// Dashboard
import Dashboard from "@/pages/dashboard/Dashboard";
import Instances from "@/pages/dashboard/Instances";
import CreateInstance from "@/pages/dashboard/CreateInstance";
import InstanceDetails from "@/pages/dashboard/InstanceDetails";
import RDPAccess from "@/pages/dashboard/RDPAccess";
import SkillsMarketplace from "@/pages/dashboard/SkillsMarketplace";
import BillingOverview from "@/pages/dashboard/BillingOverview";
import Invoices from "@/pages/dashboard/Invoices";
import Credits from "@/pages/dashboard/Credits";
import Settings from "@/pages/dashboard/Settings";
import Support from "@/pages/dashboard/Support";

// Admin pages (Superuser only)
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminStatus from "@/pages/admin/AdminStatus";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAllInstances from "@/pages/admin/AdminAllInstances";

// Info pages
import About from "@/pages/info/About";
import Docs from "@/pages/info/Docs";
import BlogDetail from "@/pages/info/BlogDetail";
import Blog from "@/pages/info/Blog";
import Status from "@/pages/info/Status";
import Contact from "@/pages/info/Contact";
import Terms from "@/pages/info/Terms";
import Privacy from "@/pages/info/Privacy";
import Cookies from "@/pages/info/Cookies";
import Refund from "@/pages/info/Refund";
import Shipping from "@/pages/info/Shipping";

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/signin" replace />;
  return children;
}

function PublicPage({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function DashboardPage({ children }) {
  return (
    <ProtectedRoute>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
      // Track page view on route change
      trackPageView(pathname, document.title);
    }
  }, [pathname]);
  
  return null;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicPage><Landing /></PublicPage>} />
        <Route path="/features" element={<PublicPage><Features /></PublicPage>} />
        <Route path="/pricing" element={<PublicPage><Pricing /></PublicPage>} />

        {/* Auth */}
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Onboarding */}
        <Route path="/onboarding/step-1" element={<Step1 />} />
        <Route path="/onboarding/step-2" element={<Step2 />} />
        <Route path="/onboarding/step-3" element={<Step3 />} />
        <Route path="/onboarding/step-4" element={<Step4 />} />

        {/* Dashboard */}
        <Route path="/dashboard" element={<DashboardPage><Dashboard /></DashboardPage>} />
        <Route path="/dashboard/instances" element={<DashboardPage><Instances /></DashboardPage>} />
        <Route path="/dashboard/instances/create" element={<DashboardPage><CreateInstance /></DashboardPage>} />
        <Route path="/dashboard/instances/:id" element={<DashboardPage><InstanceDetails /></DashboardPage>} />
        <Route path="/dashboard/instances/:id/rdp" element={<DashboardPage><RDPAccess /></DashboardPage>} />
        <Route path="/dashboard/skills" element={<DashboardPage><SkillsMarketplace /></DashboardPage>} />
        <Route path="/dashboard/billing" element={<DashboardPage><BillingOverview /></DashboardPage>} />
        <Route path="/dashboard/billing/invoices" element={<DashboardPage><Invoices /></DashboardPage>} />
        <Route path="/dashboard/billing/credits" element={<DashboardPage><Credits /></DashboardPage>} />
        <Route path="/dashboard/settings" element={<DashboardPage><Settings /></DashboardPage>} />
        <Route path="/dashboard/support" element={<DashboardPage><Support /></DashboardPage>} />

        {/* Admin Routes (Superuser only - access controlled in components) */}
        <Route path="/dashboard/admin" element={<DashboardPage><AdminOverview /></DashboardPage>} />
        <Route path="/dashboard/admin/users" element={<DashboardPage><AdminUsers /></DashboardPage>} />
        <Route path="/dashboard/admin/instances" element={<DashboardPage><AdminAllInstances /></DashboardPage>} />
        <Route path="/dashboard/admin/blog" element={<DashboardPage><AdminBlog /></DashboardPage>} />
        <Route path="/dashboard/admin/status" element={<DashboardPage><AdminStatus /></DashboardPage>} />
        <Route path="/dashboard/admin/config" element={<DashboardPage><AdminConfig /></DashboardPage>} />
        <Route path="/dashboard/admin/analytics" element={<DashboardPage><AdminAnalytics /></DashboardPage>} />
        <Route path="/dashboard/admin/pricing" element={<DashboardPage><AdminPricing /></DashboardPage>} />

        {/* Info */}
        <Route path="/about" element={<PublicPage><About /></PublicPage>} />
        <Route path="/docs" element={<PublicPage><Docs /></PublicPage>} />
        <Route path="/blog" element={<PublicPage><Blog /></PublicPage>} />
        <Route path="/blog/:slug" element={<PublicPage><BlogDetail /></PublicPage>} />
        <Route path="/system-status" element={<PublicPage><Status /></PublicPage>} />
        <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />

        {/* Legal */}
        <Route path="/legal/terms" element={<PublicPage><Terms /></PublicPage>} />
        <Route path="/legal/privacy" element={<PublicPage><Privacy /></PublicPage>} />
        <Route path="/legal/cookies" element={<PublicPage><Cookies /></PublicPage>} />
        <Route path="/legal/refund" element={<PublicPage><Refund /></PublicPage>} />
        <Route path="/legal/shipping" element={<PublicPage><Shipping /></PublicPage>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <GoogleAnalytics />
          <NotificationProvider>
            <AppRoutes />
            <NotificationToast />
          </NotificationProvider>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
